import {computed} from "vue";
// https://www.npmjs.com/package/jsonpath
import jp from "jsonpath";
import {getStoreByName} from "./storeHelpers";

export const mandatory_props = {
    json_path: {
        type: String,
    },
    label: {
        type: String,
        required: true,
    },
    hint: {
        type: String,
    },
    component_config: {
        type: Object,
    },
    vuetify_config: {
        type: Object,
    },
    storeName: {
        type: String,
        default: 'StepEditStore'
    },
    jsonKey: {
        type: String,
        default: 'content'
    }
};

/**
 * Components that always want the full width of the editor grid.
 * Everything else is laid out in columns as soon as there is enough room.
 *
 * @see resources/css/app.scss -> .editor-fields
 * @type {string[]}
 */
const FULL_WIDTH_COMPONENTS = [
    'actionfield',
    'actionbutton',
    'actionbuttonfield',
    'alert',
    'alertfield',
    'ingredientfield',
    'mediafield',
    'quizanswer',
    'quizanswers',
    'quizanswerfield',
    'quizanswersfield',
    'answerfield',
    'answersfield',
    'richtext',
    'richtextfield',
    'tiptap',
    'subpanel',
    'textareafield',
];

/**
 * Should the given editor config item span the whole editor width?
 *
 * Can be forced per field via component_config:
 * "component_config": {"full_width": true}
 *
 * @param {Object} item     a single editor config item
 * @param {boolean} repeatable
 * @returns {boolean}
 */
export function isFullWidthField(item, repeatable = false) {
    if (item?.component_config && typeof item.component_config.full_width !== 'undefined') {
        return !!item.component_config.full_width;
    }

    // Repeatable fields render as cards with lists and need the room.
    if (repeatable) return true;

    if ( ! item?.component) return false;

    return FULL_WIDTH_COMPONENTS.includes(String(item.component).toLowerCase());
}

/**
 * Split a JSONPath into its single segments.
 *
 * Editor configs use both absolute ("$.assets.doors") and
 * relative ("assets.doors") paths, jsonpath understands both.
 *
 * @param {string|null} path
 * @returns {Array}
 */
function pathSegments(path) {
    if (path === null || typeof path === 'undefined' || path === '') return [];

    try {
        return jp.parse(path).map(e => e.expression.value);
    } catch (e) {
        console.warn('Could not parse json_path', path, e); // i18n-ignore (Konsole)
        return [];
    }
}

/**
 * Build the effective JSONPath of a field.
 *
 * If the field lives *within* a repeatable SubPanel, the json_path from the
 * config is only the part below the currently opened repeater item. The
 * missing prefix (panel path + current index) is prepended here.
 *
 * @param {Object} store            StepEditStore or ComponentEditStore
 * @param {string|null} own_path    json_path as configured on the field
 * @returns {string|null}
 */
export function resolveFieldPath(store, own_path) {
    const window_props = store?.current_window_props;

    if ( ! window_props || ! window_props.json_path) {
        return own_path || null;
    }

    const segments = [
        ...pathSegments(window_props.json_path),
        window_props.repeater_index,
        // A nested path must not carry its own root ("$"), it would end up
        // as a literal key in the middle of the path: $.foo[0]["$"].bar
        ...pathSegments(own_path).filter(segment => segment !== '$'),
    ].filter(segment => segment !== null && typeof segment !== 'undefined');

    try {
        return jp.stringify(segments);
    } catch (e) {
        console.warn('Could not stringify json_path', segments, e); // i18n-ignore (Konsole)
        return own_path || null;
    }
}

/**
 * Shared plumbing for every editor field component.
 *
 * Resolves the store, the effective json_path and gives read/write access to
 * the edited value - honouring the "jsonKey" prop, so the very same fields can
 * edit the "content" JSON as well as the "info" JSON of a step.
 *
 * @param {Object} props    the components props (see mandatory_props)
 * @returns {{store: Object, jsonPath: ComputedRef<string|null>, value: WritableComputedRef<*>, childValue: (function(string): WritableComputedRef<*>), readValue: (function(string|null): *), writeValue: (function(string|null, *): void)}}
 */
export function useEditorField(props) {
    const store = getStoreByName(props.storeName);

    const jsonPath = computed(() => resolveFieldPath(store, props.json_path));

    /**
     * the JSON that is currently edited: "content" or "info"
     */
    function root() {
        return store[props.jsonKey];
    }

    function readValue(path) {
        if ( ! path) return null;

        try {
            return jp.value(root(), path);
        } catch (e) {
            console.warn('Could not read value for json_path', path, e); // i18n-ignore (Konsole)
            return null;
        }
    }

    function writeValue(path, v) {
        if ( ! path) return;

        try {
            jp.value(root(), path, v);
        } catch (e) {
            console.warn('Could not set value for json_path', path, e); // i18n-ignore (Konsole)
        }
    }

    const value = computed({
        get() {
            return readValue(jsonPath.value);
        },
        set(v) {
            writeValue(jsonPath.value, v);
        }
    });

    /**
     * a single key below the fields json_path, e.g. "url" or "type"
     *
     * @param {string} key
     * @returns {WritableComputedRef<*>}
     */
    function childValue(key) {
        return computed({
            get() {
                return readValue(jsonPath.value ? `${jsonPath.value}.${key}` : null);
            },
            set(v) {
                writeValue(jsonPath.value ? `${jsonPath.value}.${key}` : null, v);
            }
        });
    }

    return {store, jsonPath, value, childValue, readValue, writeValue};
}

// ----------------------------------------------------------------------------
// Gemeinsame Feldsprache des Kundeneditors
//
// Alle Controls verwenden denselben visuellen Variant und dieselbe Dichte.
// Label, Hint und Fehler werden nicht mehr vom Control gerendert, sondern von
// EditorFieldFrame - deshalb "hide-details" und kein "label"/"hint".
//
// @see resources/js/components/editor-fields/EditorFieldFrame.vue
// @see resources/css/components/editor.scss
// ----------------------------------------------------------------------------
export const EDITOR_CONTROL_DEFAULTS = {
    variant: 'outlined',
    density: 'comfortable',
    hideDetails: true,
};

/**
 * Funktionale vuetify_config-Schlüssel, die eine Editor-Definition setzen darf.
 *
 * Bewusst eine Positivliste: Eine App-Definition soll das VERHALTEN eines
 * Feldes beschreiben können (Auswahlwerte, Zeilenzahl, Eingabetyp), aber nicht
 * das AUSSEHEN. Freie Styling-Props waren eine Hauptquelle für uneinheitliche
 * Oberflächen - und enthalten historisch sogar Tippfehler wie "witdth".
 *
 * Alles, was nicht in dieser Liste steht, wird ignoriert und in der Konsole
 * gemeldet - es verschwindet nicht still.
 */
const ALLOWED_CONTROL_PROPS = [
    // Textliche Eingaben
    'type', 'placeholder', 'prefix', 'suffix', 'rows', 'autoGrow', 'auto-grow',
    'counter', 'maxlength', 'clearable',
    // Auswahl
    'items', 'itemTitle', 'item-title', 'itemValue', 'item-value', 'multiple', 'chips',
    // Zustand
    'readonly', 'disabled',
    // Zahl
    'min', 'max', 'step',
];

const ALLOWED_LOOKUP = new Set(ALLOWED_CONTROL_PROPS.map(key => key.toLowerCase()));

/**
 * Bereits gemeldete unerlaubte Props - verhindert Konsolen-Spam bei jedem
 * Re-Render.
 */
const reportedDroppedProps = new Set();

/**
 * Props für ein Editor-Control zusammenstellen.
 *
 * @param {Object|Array|null} vuetify_config   Rohwert aus der Editor-Definition
 * @param {Object} overrides                   feldspezifische Ergänzungen
 * @returns {Object}
 */
export function editorControlProps(vuetify_config, overrides = {}) {
    const allowed = {};

    // Historisch steht dort teilweise eine (leere) Liste statt eines Objekts.
    if (vuetify_config && typeof vuetify_config === 'object' && ! Array.isArray(vuetify_config)) {
        Object.entries(vuetify_config).forEach(([key, value]) => {
            if (ALLOWED_LOOKUP.has(String(key).toLowerCase())) {
                allowed[key] = value;

                return;
            }

            if ( ! reportedDroppedProps.has(key)) {
                reportedDroppedProps.add(key);
                console.info(
                    `[Editor] Die Eigenschaft "${key}" aus vuetify_config wird im Kundeneditor ` // i18n-ignore (Konsole)
                    + 'nicht angewendet. Das Erscheinungsbild wird zentral festgelegt.'
                );
            }
        });
    }

    return {
        ...EDITOR_CONTROL_DEFAULTS,
        ...allowed,
        ...overrides,
    };
}

/**
 * Stabile Kennung für die Label-Control-Verknüpfung eines Feldes.
 */
export function editorControlId(prefix, jsonPath) {
    const suffix = String(jsonPath ?? 'feld')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();

    return `${prefix}-${suffix || 'feld'}`;
}
